/**
 * Crypto Trading Page
 * Professional crypto analysis with multi-timeframe indicators,
 * regime detection, and three risk profiles
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cryptoAPI, botAPI, cryptoNewsAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  RefreshCw, TrendingUp, TrendingDown, Activity,
  Target, AlertTriangle, CheckCircle, XCircle, Search,
  BarChart3, Zap, Shield, Clock, DollarSign, Percent,
  ChevronDown, ChevronUp, Info, Bot, Settings, Save,
  Bitcoin, Coins, PieChart, Gauge, Newspaper, ExternalLink,
  Bell, Flame, MessageCircle, Filter
} from 'lucide-react';

// Risk Profile Card Component
const RiskProfileCard = ({ profile, isSelected, onClick }) => {
  const colors = {
    high: 'border-accent-red text-accent-red bg-accent-red/10',
    medium: 'border-accent-yellow text-accent-yellow bg-accent-yellow/10',
    low: 'border-accent-green text-accent-green bg-accent-green/10',
  };

  return (
    <div
      onClick={onClick}
      className={`card p-4 cursor-pointer transition-all ${
        isSelected ? `ring-2 ring-offset-2 ring-offset-bg-primary ${colors[profile.key]}` : ''
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold">{profile.name}</h4>
        <span className={`text-xs px-2 py-0.5 rounded border ${colors[profile.key]}`}>
          {profile.key.toUpperCase()}
        </span>
      </div>
      <p className="text-text-muted text-sm mb-3">{profile.strategy}</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-text-muted">Risk/Trade:</span>
          <span className="ml-1 font-mono">{profile.riskPerTrade}</span>
        </div>
        <div>
          <span className="text-text-muted">Max Loss:</span>
          <span className="ml-1 font-mono">{profile.maxDailyLoss}</span>
        </div>
        <div>
          <span className="text-text-muted">Positions:</span>
          <span className="ml-1 font-mono">{profile.maxPositions}</span>
        </div>
        <div>
          <span className="text-text-muted">Leverage:</span>
          <span className="ml-1 font-mono">{profile.maxLeverage}x</span>
        </div>
      </div>
    </div>
  );
};

// Market Regime Badge Component
const RegimeBadge = ({ regime }) => {
  const trendColors = {
    bullish: 'bg-accent-green/15 text-accent-green border-accent-green/30',
    bearish: 'bg-accent-red/15 text-accent-red border-accent-red/30',
    neutral: 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/30',
  };

  const volatilityColors = {
    low: 'bg-accent-blue/15 text-accent-blue',
    normal: 'bg-text-muted/15 text-text-secondary',
    high: 'bg-accent-purple/15 text-accent-purple',
  };

  const riskModeColors = {
    'risk-on': 'bg-accent-green/15 text-accent-green',
    'risk-off': 'bg-accent-red/15 text-accent-red',
    neutral: 'bg-text-muted/15 text-text-secondary',
  };

  return (
    <div className="flex flex-wrap gap-2">
      <span className={`px-2 py-1 rounded text-xs border ${trendColors[regime?.trend] || trendColors.neutral}`}>
        {regime?.trend?.toUpperCase() || 'NEUTRAL'}
      </span>
      <span className={`px-2 py-1 rounded text-xs ${volatilityColors[regime?.volatility] || volatilityColors.normal}`}>
        {regime?.volatility?.toUpperCase() || 'NORMAL'} VOL
      </span>
      <span className={`px-2 py-1 rounded text-xs ${riskModeColors[regime?.riskMode] || riskModeColors.neutral}`}>
        {regime?.riskMode?.replace('-', ' ').toUpperCase() || 'NEUTRAL'}
      </span>
      {regime?.trendStrength && (
        <span className={`px-2 py-1 rounded text-xs ${
          regime.trendStrength === 'strong' ? 'bg-accent-green/15 text-accent-green' : 'bg-text-muted/15 text-text-muted'
        }`}>
          {regime.trendStrength.toUpperCase()} TREND
        </span>
      )}
    </div>
  );
};

// Signal Card Component
const SignalCard = ({ signal, symbol }) => {
  if (!signal || signal.action === 'none') {
    return (
      <div className="p-4 bg-bg-tertiary rounded-lg text-center">
        <XCircle className="w-8 h-8 text-text-muted mx-auto mb-2" />
        <p className="text-text-muted text-sm">No trade signal</p>
        {signal?.reasons?.length > 0 && (
          <ul className="mt-2 text-xs text-text-muted space-y-1">
            {signal.reasons.slice(0, 3).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const isLong = signal.direction === 'long';
  const isDCA = signal.action === 'dca';

  return (
    <div className={`p-4 rounded-lg border ${
      isDCA ? 'bg-accent-blue/10 border-accent-blue/30' :
      isLong ? 'bg-accent-green/10 border-accent-green/30' : 'bg-accent-red/10 border-accent-red/30'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isDCA ? (
            <PieChart className="w-5 h-5 text-accent-blue" />
          ) : isLong ? (
            <TrendingUp className="w-5 h-5 text-accent-green" />
          ) : (
            <TrendingDown className="w-5 h-5 text-accent-red" />
          )}
          <span className={`font-bold ${
            isDCA ? 'text-accent-blue' : isLong ? 'text-accent-green' : 'text-accent-red'
          }`}>
            {isDCA ? 'DCA' : isLong ? 'LONG' : 'SHORT'} {symbol}
          </span>
        </div>
        <span className="text-sm font-mono">
          {signal.confidence}% confidence
        </span>
      </div>

      {signal.entry && (
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <span className="text-text-muted text-xs">Entry</span>
            <p className="font-mono">${signal.entry?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-text-muted text-xs">Stop Loss</span>
            <p className="font-mono text-accent-red">${signal.stopLoss?.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-text-muted text-xs">Risk</span>
            <p className="font-mono">
              ${Math.abs(signal.entry - signal.stopLoss).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {signal.takeProfit?.length > 0 && (
        <div className="mb-3">
          <span className="text-text-muted text-xs">Take Profits</span>
          <div className="flex gap-2 mt-1">
            {signal.takeProfit.map((tp, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-accent-green/20 text-accent-green rounded">
                TP{i + 1}: ${tp.price?.toFixed(2)} ({(tp.size * 100).toFixed(0)}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {signal.dcaMultiplier && (
        <div className="text-sm">
          <span className="text-text-muted">DCA Multiplier: </span>
          <span className="font-mono text-accent-blue">{signal.dcaMultiplier}x</span>
        </div>
      )}

      {signal.reasons?.length > 0 && (
        <ul className="mt-2 text-xs text-text-secondary space-y-1">
          {signal.reasons.map((r, i) => (
            <li key={i} className="flex items-start gap-1">
              <CheckCircle className="w-3 h-3 mt-0.5 text-accent-green flex-shrink-0" />
              {r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Scanner Result Row
const ScannerRow = ({ result, onAnalyze }) => {
  const hasSignal = result.signal?.action !== 'none' && !result.error;

  return (
    <tr className="border-b border-border-color hover:bg-bg-tertiary transition-all">
      <td className="py-3 px-4">
        <span className="font-mono font-bold">{result.symbol?.replace('/USD', '')}</span>
      </td>
      <td className="py-3 px-4">
        <span className="font-mono">${result.currentPrice?.toFixed(2)}</span>
      </td>
      <td className="py-3 px-4">
        <RegimeBadge regime={result.regime} />
      </td>
      <td className="py-3 px-4">
        <span className="font-mono">{result.indicators?.rsi}</span>
      </td>
      <td className="py-3 px-4">
        <span className="font-mono">{result.indicators?.atrPercent?.toFixed(2)}%</span>
      </td>
      <td className="py-3 px-4">
        {hasSignal ? (
          <span className={`badge ${
            result.signal.action === 'buy' || result.signal.action === 'dca' ? 'badge-green' :
            result.signal.action === 'sell' ? 'badge-red' : 'badge-yellow'
          }`}>
            {result.signal.action.toUpperCase()}
          </span>
        ) : (
          <span className="text-text-muted text-sm">-</span>
        )}
      </td>
      <td className="py-3 px-4">
        <button
          onClick={() => onAnalyze(result.symbol)}
          className="btn-secondary text-sm py-1 px-3"
        >
          Analyze
        </button>
      </td>
    </tr>
  );
};

// Fee Tier Display
const FeeTierDisplay = ({ feeTier }) => {
  if (!feeTier) return null;

  return (
    <div className="card p-4">
      <h4 className="font-semibold mb-3 flex items-center gap-2">
        <Percent className="w-4 h-4 text-accent-blue" />
        Fee Tier
      </h4>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-text-muted">Current Tier</span>
          <p className="font-mono text-lg">Level {feeTier.currentTier}</p>
        </div>
        <div>
          <span className="text-text-muted">Maker Fee</span>
          <p className="font-mono text-accent-green">{feeTier.makerFee}</p>
        </div>
        <div>
          <span className="text-text-muted">Taker Fee</span>
          <p className="font-mono text-accent-yellow">{feeTier.takerFee}</p>
        </div>
      </div>
      {feeTier.volumeToNextTier > 0 && (
        <p className="text-xs text-text-muted mt-2">
          ${feeTier.volumeToNextTier.toLocaleString()} more volume to reach Tier {feeTier.currentTier + 1}
        </p>
      )}
    </div>
  );
};

// Main Crypto Page
const Crypto = () => {
  const { hasAlpacaKeys } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Analysis state
  const [symbol, setSymbol] = useState('BTC');
  const [analysis, setAnalysis] = useState(null);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('medium');

  // Scanner state
  const [scanResults, setScanResults] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedUniverse, setSelectedUniverse] = useState('curated');

  // Universe state
  const [universes, setUniverses] = useState(null);

  // Fee tier state
  const [feeTier, setFeeTier] = useState(null);

  // Bot config state
  const [botConfig, setBotConfig] = useState({
    cryptoEnabled: false,
    cryptoConfig: {
      riskProfile: 'medium',
      universe: 'curated',
      
      // Position limits per profile
      maxPositions: { high: 5, medium: 4, low: 3 },
      
      // Risk management per profile
      maxDailyLoss: { high: 0.06, medium: 0.03, low: 0.015 },
      maxAccountDrawdown: { high: 0.25, medium: 0.15, low: 0.08 },
      maxMarginUsed: { high: 0.75, medium: 0.50, low: 0.30 },
      riskPerTrade: { high: 0.0125, medium: 0.005, low: 0.0025 },
      
      // Leverage caps
      leverageCaps: {
        high: { BTC: 8, ETH: 6, alts: 4 },
        medium: { BTC: 4, ETH: 3, alts: 2 },
        low: { BTC: 1, ETH: 1, alts: 1 },
      },
      
      // Correlation gating
      correlationGatingEnabled: true,
      
      // Kill switch
      killSwitchEnabled: true,
      killSwitch: {
        maxSlippageMultiplier: 3,
        maxFundingRate: 0.0015,
        maxLatencyMs: 5000,
        minLiquidationDistance: 1.5,
      },
      
      // High risk settings
      highRisk: {
        bbwPercentile: 20,
        volumeZScore: 1.5,
        stopLossATR: 1.5,
        trailingATR: 3.0,
        maxReentries: 2,
      },
      
      // Medium risk settings
      mediumRisk: {
        adxThreshold: 20,
        stopLossATR: 1.2,
        trailingATR: 2.5,
      },
      
      // Low risk settings
      lowRisk: {
        allowShorts: false,
        coreAllocation: 0.80,
        stableBuffer: 0.10,
        riskOnATRPercent: 4.0,
        riskOffATRPercent: 5.5,
      },
      
      // DCA settings
      enableDCA: true,
      dcaFrequency: 'daily',
      
      // Rebalancing
      rebalanceFrequency: 'weekly',
      rebalanceBand: 0.20,
      
      // Safety
      ema200Filter: true,
    }
  });
  const [savingBotConfig, setSavingBotConfig] = useState(false);
  const [showBotConfig, setShowBotConfig] = useState(false);
  const [configTab, setConfigTab] = useState('general'); // 'general', 'risk', 'profiles', 'safety'

  // News & Sentiment state
  const [showNews, setShowNews] = useState(false);
  const [newsTab, setNewsTab] = useState('sentiment'); // 'sentiment', 'feed', 'signals', 'trending'
  const [marketSentiment, setMarketSentiment] = useState(null);
  const [newsFeed, setNewsFeed] = useState([]);
  const [newsSignals, setNewsSignals] = useState(null);
  const [trending, setTrending] = useState(null);
  const [loadingNews, setLoadingNews] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [profilesRes, universeRes, feeTierRes] = await Promise.all([
          cryptoAPI.getProfiles().catch(() => null),
          cryptoAPI.getUniverse().catch(() => null),
          cryptoAPI.getFeeTier().catch(() => null),
        ]);

        if (profilesRes?.data) {
          setRiskProfiles(profilesRes.data.profiles);
        }
        if (universeRes?.data) {
          setUniverses(universeRes.data.universes);
        }
        if (feeTierRes?.data) {
          setFeeTier(feeTierRes.data);
        }

        // Fetch bot config
        await fetchBotConfig();
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (hasAlpacaKeys) {
      fetchInitialData();
    } else {
      setLoading(false);
    }
  }, [hasAlpacaKeys]);

  // Fetch bot config
  const fetchBotConfig = async () => {
    try {
      const res = await botAPI.getConfig();
      if (res.data?.config?.cryptoConfig) {
        const cc = res.data.config.cryptoConfig;
        setBotConfig({
          cryptoEnabled: res.data.config.cryptoEnabled || false,
          cryptoConfig: {
            riskProfile: cc.riskProfile || 'medium',
            universe: cc.universe || 'curated',
            maxPositions: cc.maxPositions || { high: 5, medium: 4, low: 3 },
            maxDailyLoss: cc.maxDailyLoss || { high: 0.06, medium: 0.03, low: 0.015 },
            maxAccountDrawdown: cc.maxAccountDrawdown || { high: 0.25, medium: 0.15, low: 0.08 },
            maxMarginUsed: cc.maxMarginUsed || { high: 0.75, medium: 0.50, low: 0.30 },
            riskPerTrade: cc.riskPerTrade || { high: 0.0125, medium: 0.005, low: 0.0025 },
            leverageCaps: cc.leverageCaps || {
              high: { BTC: 8, ETH: 6, alts: 4 },
              medium: { BTC: 4, ETH: 3, alts: 2 },
              low: { BTC: 1, ETH: 1, alts: 1 },
            },
            correlationGatingEnabled: cc.correlationGatingEnabled !== false,
            killSwitchEnabled: cc.killSwitchEnabled !== false,
            killSwitch: cc.killSwitch || {
              maxSlippageMultiplier: 3,
              maxFundingRate: 0.0015,
              maxLatencyMs: 5000,
              minLiquidationDistance: 1.5,
            },
            highRisk: cc.highRisk || { bbwPercentile: 20, volumeZScore: 1.5, stopLossATR: 1.5, trailingATR: 3.0, maxReentries: 2 },
            mediumRisk: cc.mediumRisk || { adxThreshold: 20, stopLossATR: 1.2, trailingATR: 2.5 },
            lowRisk: cc.lowRisk || { allowShorts: false, coreAllocation: 0.80, stableBuffer: 0.10, riskOnATRPercent: 4.0, riskOffATRPercent: 5.5 },
            enableDCA: cc.enableDCA !== false,
            dcaFrequency: cc.dcaFrequency || 'daily',
            rebalanceFrequency: cc.rebalanceFrequency || 'weekly',
            rebalanceBand: cc.rebalanceBand || 0.20,
            ema200Filter: cc.ema200Filter !== false,
          }
        });
      }
    } catch (err) {
      console.error('Failed to fetch bot config:', err);
    }
  };

  // Save bot config
  const saveBotConfig = async () => {
    setSavingBotConfig(true);
    try {
      await botAPI.updateConfig(botConfig);
      toast.success('Crypto bot settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error(err);
    } finally {
      setSavingBotConfig(false);
    }
  };

  // Update crypto config
  const updateCryptoConfig = (key, value) => {
    setBotConfig(prev => ({
      ...prev,
      cryptoConfig: {
        ...prev.cryptoConfig,
        [key]: value
      }
    }));
  };

  // Fetch news data
  const fetchNewsData = async () => {
    setLoadingNews(true);
    try {
      const [sentimentRes, feedRes, signalsRes, trendingRes] = await Promise.all([
        cryptoNewsAPI.getMarketSentiment(24).catch(() => null),
        cryptoNewsAPI.getFeed(24, 30).catch(() => null),
        cryptoNewsAPI.getSignals(60, 24).catch(() => null),
        cryptoNewsAPI.getTrending(6).catch(() => null),
      ]);

      if (sentimentRes?.data) setMarketSentiment(sentimentRes.data);
      if (feedRes?.data) setNewsFeed(feedRes.data.articles || []);
      if (signalsRes?.data) setNewsSignals(signalsRes.data);
      if (trendingRes?.data) setTrending(trendingRes.data);
    } catch (err) {
      console.error('Failed to fetch news:', err);
    } finally {
      setLoadingNews(false);
    }
  };

  // Analyze symbol
  const analyzeSymbol = useCallback(async (sym = symbol) => {
    if (!sym) return;

    setAnalyzing(true);
    try {
      const formattedSymbol = sym.includes('/') ? sym : `${sym}/USD`;
      const res = await cryptoAPI.analyze(formattedSymbol, selectedProfile);
      setAnalysis(res.data);
      setSymbol(sym.replace('/USD', ''));
      toast.success(`Analysis complete for ${sym}`);
    } catch (err) {
      toast.error('Analysis failed');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  }, [symbol, selectedProfile]);

  // Run scanner
  const runScanner = async () => {
    setScanning(true);
    try {
      const res = await cryptoAPI.scan(selectedUniverse, selectedProfile);
      setScanResults(res.data);
      setShowScanner(true);
      toast.success(`Scanned ${res.data.totalScanned} symbols`);
    } catch (err) {
      toast.error('Scanner failed');
      console.error(err);
    } finally {
      setScanning(false);
    }
  };

  if (!hasAlpacaKeys) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-accent-yellow mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Alpaca Required</h2>
          <p className="text-text-muted mb-4">Connect your Alpaca account to use crypto trading.</p>
          <Link to="/settings" className="btn-primary">Go to Settings</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation 
        title="Crypto Trading" 
        subtitle="Multi-timeframe analysis with risk profiles"
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Action Bar */}
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={runScanner}
            disabled={scanning}
            className="btn-secondary flex items-center gap-2"
          >
            {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {scanning ? 'Scanning...' : 'Scan Market'}
          </button>
        </div>

      {/* Risk Profile Selection */}
      <div className="mb-6">
        <h3 className="font-display text-lg font-semibold mb-4">Select Risk Profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {riskProfiles.map(profile => (
            <RiskProfileCard
              key={profile.key}
              profile={profile}
              isSelected={selectedProfile === profile.key}
              onClick={() => setSelectedProfile(profile.key)}
            />
          ))}
        </div>
      </div>

      {/* Symbol Input */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Symbol</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="BTC"
                className="input flex-1 font-mono"
                onKeyPress={(e) => e.key === 'Enter' && analyzeSymbol()}
              />
              <button
                onClick={() => analyzeSymbol()}
                disabled={analyzing || !symbol}
                className="btn-primary px-6"
              >
                {analyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Analyze'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Quick Select</label>
            <div className="flex gap-2 flex-wrap">
              {['BTC', 'ETH', 'SOL', 'AVAX', 'LINK', 'MATIC'].map(s => (
                <button
                  key={s}
                  onClick={() => { setSymbol(s); analyzeSymbol(s); }}
                  className="px-3 py-2 rounded-lg bg-bg-tertiary text-text-secondary hover:bg-accent-yellow/15 hover:text-accent-yellow transition-all text-sm font-mono"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Results */}
      {analysis && !analysis.error && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Price & Regime */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Coins className="w-5 h-5 text-accent-yellow" />
              {analysis.symbol}
            </h3>
            <div className="text-3xl font-mono font-bold mb-4">
              ${analysis.currentPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
            <div className="mb-4">
              <span className="text-text-muted text-sm mb-2 block">Market Regime</span>
              <RegimeBadge regime={analysis.regime} />
            </div>
            <div className="text-xs text-text-muted">
              Profile: {analysis.profile}
            </div>
          </div>

          {/* Indicators */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent-blue" />
              Indicators
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">RSI (14)</span>
                <span className={`font-mono ${
                  analysis.indicators?.rsi > 70 ? 'text-accent-red' :
                  analysis.indicators?.rsi < 30 ? 'text-accent-green' : ''
                }`}>{analysis.indicators?.rsi}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">ADX</span>
                <span className={`font-mono ${
                  analysis.indicators?.adx >= 18 ? 'text-accent-green' : 'text-text-muted'
                }`}>{analysis.indicators?.adx}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">ATR %</span>
                <span className="font-mono">{analysis.indicators?.atrPercent}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">BBW %ile</span>
                <span className="font-mono">{analysis.indicators?.bbwPercentile}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Vol Z-Score</span>
                <span className={`font-mono ${
                  analysis.indicators?.volumeZScore >= 1.5 ? 'text-accent-green' : ''
                }`}>{analysis.indicators?.volumeZScore}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Drawdown</span>
                <span className={`font-mono ${
                  analysis.indicators?.drawdown > 10 ? 'text-accent-red' : ''
                }`}>{analysis.indicators?.drawdown}%</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border-color">
              <div className="text-xs text-text-muted space-y-1">
                <div className="flex justify-between">
                  <span>EMA 20:</span>
                  <span className="font-mono">${analysis.indicators?.ema20}</span>
                </div>
                <div className="flex justify-between">
                  <span>EMA 50:</span>
                  <span className="font-mono">${analysis.indicators?.ema50}</span>
                </div>
                <div className="flex justify-between">
                  <span>EMA 200:</span>
                  <span className="font-mono">${analysis.indicators?.ema200}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Signal */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-accent-green" />
              Trading Signal
            </h3>
            <SignalCard signal={analysis.signal} symbol={analysis.symbol?.replace('/USD', '')} />
          </div>
        </div>
      )}

      {/* Scanner Results */}
      {showScanner && scanResults && (
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Search className="w-5 h-5 text-accent-blue" />
              Scanner Results ({scanResults.opportunities} opportunities)
            </h3>
            <div className="flex items-center gap-3">
              <select
                value={selectedUniverse}
                onChange={(e) => setSelectedUniverse(e.target.value)}
                className="input text-sm py-1"
              >
                <option value="core">Core (BTC/ETH)</option>
                <option value="majors">Majors</option>
                <option value="curated">Full List</option>
              </select>
              <button
                onClick={() => setShowScanner(false)}
                className="text-text-muted hover:text-text-primary"
              >
                Hide
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-text-muted text-sm border-b border-border-color">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Price</th>
                  <th className="py-3 px-4">Regime</th>
                  <th className="py-3 px-4">RSI</th>
                  <th className="py-3 px-4">ATR%</th>
                  <th className="py-3 px-4">Signal</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {[...scanResults.results.opportunities, ...scanResults.results.noSignal].slice(0, 10).map((result, i) => (
                  <ScannerRow
                    key={i}
                    result={result}
                    onAnalyze={(sym) => { setSymbol(sym.replace('/USD', '')); analyzeSymbol(sym); setShowScanner(false); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Crypto News & Sentiment Section */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Newspaper className="w-6 h-6 text-accent-blue" />
            <div>
              <h3 className="font-display text-lg font-semibold">Crypto News & Sentiment</h3>
              <p className="text-text-muted text-sm">Aggregated from 9 major crypto news sources</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchNewsData}
              disabled={loadingNews}
              className="btn-secondary flex items-center gap-2"
            >
              {loadingNews ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button
              onClick={() => { setShowNews(!showNews); if (!showNews && !marketSentiment) fetchNewsData(); }}
              className="btn-primary flex items-center gap-2"
            >
              {showNews ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showNews ? 'Hide' : 'Show'} News
            </button>
          </div>
        </div>

        {/* Quick Sentiment Overview (Always Visible) */}
        {marketSentiment && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className={`p-3 rounded-lg ${
              marketSentiment.overallSentiment?.includes('bullish') ? 'bg-accent-green/15 border border-accent-green/30' :
              marketSentiment.overallSentiment?.includes('bearish') ? 'bg-accent-red/15 border border-accent-red/30' :
              'bg-bg-tertiary'
            }`}>
              <div className="text-xs text-text-muted">Market Sentiment</div>
              <div className={`font-bold text-lg capitalize ${
                marketSentiment.overallSentiment?.includes('bullish') ? 'text-accent-green' :
                marketSentiment.overallSentiment?.includes('bearish') ? 'text-accent-red' : ''
              }`}>
                {marketSentiment.overallSentiment?.replace('_', ' ') || 'Neutral'}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-bg-tertiary">
              <div className="text-xs text-text-muted">Articles (24h)</div>
              <div className="font-bold text-lg">{marketSentiment.articleCount || 0}</div>
            </div>
            <div className="p-3 rounded-lg bg-accent-green/15">
              <div className="text-xs text-text-muted">Bullish Score</div>
              <div className="font-bold text-lg text-accent-green">{marketSentiment.bullishScore || 0}</div>
            </div>
            <div className="p-3 rounded-lg bg-accent-red/15">
              <div className="text-xs text-text-muted">Bearish Score</div>
              <div className="font-bold text-lg text-accent-red">{marketSentiment.bearishScore || 0}</div>
            </div>
          </div>
        )}

        {showNews && (
          <div className="space-y-4">
            {/* News Tabs */}
            <div className="flex gap-2 border-b border-border-color pb-2">
              {[
                { key: 'sentiment', label: 'Sentiment', icon: BarChart3 },
                { key: 'signals', label: 'Signals', icon: Target },
                { key: 'feed', label: 'News Feed', icon: Newspaper },
                { key: 'trending', label: 'Trending', icon: Flame },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setNewsTab(tab.key)}
                  className={`px-4 py-2 text-sm rounded-t-lg transition-all flex items-center gap-2 ${
                    newsTab === tab.key
                      ? 'bg-accent-blue/15 text-accent-blue border-b-2 border-accent-blue'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sentiment Tab */}
            {newsTab === 'sentiment' && marketSentiment && (
              <div className="space-y-4">
                {/* Top Coins by Mentions */}
                <div>
                  <h4 className="font-semibold mb-3">Top Coins by Mentions</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {marketSentiment.topCoins?.slice(0, 10).map((coin, i) => (
                      <div
                        key={coin.symbol}
                        className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-105 ${
                          coin.sentiment === 'bullish' ? 'bg-accent-green/10 border border-accent-green/30' :
                          coin.sentiment === 'bearish' ? 'bg-accent-red/10 border border-accent-red/30' :
                          'bg-bg-tertiary'
                        }`}
                        onClick={() => { setSymbol(coin.symbol); analyzeSymbol(coin.symbol); }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold">{coin.symbol}</span>
                          {coin.sentiment === 'bullish' ? (
                            <TrendingUp className="w-4 h-4 text-accent-green" />
                          ) : coin.sentiment === 'bearish' ? (
                            <TrendingDown className="w-4 h-4 text-accent-red" />
                          ) : null}
                        </div>
                        <div className="text-xs text-text-muted mt-1">{coin.mentions} mentions</div>
                        <div className={`text-xs font-mono mt-1 ${
                          coin.sentimentScore > 0 ? 'text-accent-green' : 
                          coin.sentimentScore < 0 ? 'text-accent-red' : ''
                        }`}>
                          {coin.sentimentScore > 0 ? '+' : ''}{coin.sentimentScore}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Source Breakdown */}
                {marketSentiment.sourceBreakdown && (
                  <div>
                    <h4 className="font-semibold mb-3">Source Breakdown</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      {marketSentiment.sourceBreakdown.slice(0, 8).map((source, i) => (
                        <div key={i} className="p-2 bg-bg-tertiary rounded flex justify-between">
                          <span className="text-text-secondary truncate">{source.source}</span>
                          <span className="font-mono">{source.articleCount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Signals Tab */}
            {newsTab === 'signals' && (
              <div className="space-y-3">
                {newsSignals?.signals?.length > 0 ? (
                  newsSignals.signals.slice(0, 10).map((signal, i) => (
                    <div
                      key={i}
                      className={`p-4 rounded-lg border ${
                        signal.action === 'buy' ? 'bg-accent-green/10 border-accent-green/30' :
                        signal.action === 'sell' ? 'bg-accent-red/10 border-accent-red/30' :
                        signal.action === 'watch_bullish' ? 'bg-accent-green/5 border-accent-green/20' :
                        signal.action === 'watch_bearish' ? 'bg-accent-red/5 border-accent-red/20' :
                        'bg-bg-tertiary'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-lg">{signal.symbol}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                            signal.action === 'buy' ? 'bg-accent-green text-white' :
                            signal.action === 'sell' ? 'bg-accent-red text-white' :
                            signal.action === 'watch_bullish' ? 'bg-accent-green/20 text-accent-green' :
                            signal.action === 'watch_bearish' ? 'bg-accent-red/20 text-accent-red' :
                            'bg-text-muted/20'
                          }`}>
                            {signal.action.replace('_', ' ')}
                          </span>
                        </div>
                        <span className="font-mono text-sm">{signal.confidence}% confidence</span>
                      </div>
                      <p className="text-sm text-text-secondary mb-2">{signal.reason}</p>
                      <div className="flex items-center gap-4 text-xs text-text-muted">
                        <span>{signal.articleCount} articles</span>
                        <span>Impact: {signal.avgImpact}</span>
                        <span className="text-accent-green">+{signal.bullishCount} bullish</span>
                        <span className="text-accent-red">-{signal.bearishCount} bearish</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-text-muted">
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No trading signals at the moment</p>
                    <p className="text-xs mt-1">Signals appear when strong sentiment patterns emerge</p>
                  </div>
                )}
              </div>
            )}

            {/* News Feed Tab */}
            {newsTab === 'feed' && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {newsFeed.length > 0 ? (
                  newsFeed.map((article, i) => (
                    <a
                      key={i}
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 bg-bg-tertiary rounded-lg hover:bg-bg-card transition-all"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h5 className="font-medium text-sm mb-1 line-clamp-2">{article.title}</h5>
                          <div className="flex items-center gap-2 text-xs text-text-muted">
                            <span className="text-accent-blue">{article.source}</span>
                            <span>•</span>
                            <span>{new Date(article.publishedAt).toLocaleString()}</span>
                          </div>
                          {article.mentionedCoins?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {article.mentionedCoins.slice(0, 5).map(coin => (
                                <span key={coin} className="px-1.5 py-0.5 bg-accent-yellow/15 text-accent-yellow text-xs rounded">
                                  {coin}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-1 rounded text-xs ${
                            article.sentiment?.sentiment?.includes('bullish') ? 'bg-accent-green/15 text-accent-green' :
                            article.sentiment?.sentiment?.includes('bearish') ? 'bg-accent-red/15 text-accent-red' :
                            'bg-text-muted/15 text-text-muted'
                          }`}>
                            {article.sentiment?.sentiment?.replace('_', ' ') || 'neutral'}
                          </span>
                          <span className="text-xs text-text-muted">Impact: {article.impactScore}</span>
                          <ExternalLink className="w-4 h-4 text-text-muted" />
                        </div>
                      </div>
                    </a>
                  ))
                ) : (
                  <div className="text-center py-8 text-text-muted">
                    <Newspaper className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No news articles loaded</p>
                    <button onClick={fetchNewsData} className="text-accent-blue text-sm mt-2">
                      Click to load news
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Trending Tab */}
            {newsTab === 'trending' && trending && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Trending Coins */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Flame className="w-4 h-4 text-accent-orange" />
                      Trending Coins (6h)
                    </h4>
                    <div className="space-y-2">
                      {trending.trendingCoins?.slice(0, 8).map((coin, i) => (
                        <div
                          key={coin.symbol}
                          className="flex items-center justify-between p-2 bg-bg-tertiary rounded cursor-pointer hover:bg-bg-card"
                          onClick={() => { setSymbol(coin.symbol); analyzeSymbol(coin.symbol); }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-text-muted text-xs w-4">#{i + 1}</span>
                            <span className="font-mono font-bold">{coin.symbol}</span>
                          </div>
                          <span className="text-xs text-text-muted">{coin.mentions} mentions</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trending Categories */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Filter className="w-4 h-4 text-accent-purple" />
                      Hot Topics
                    </h4>
                    <div className="space-y-2">
                      {trending.trendingCategories?.map((cat, i) => (
                        <div key={cat.category} className="flex items-center justify-between p-2 bg-bg-tertiary rounded">
                          <span className="capitalize">{cat.category}</span>
                          <span className="text-xs px-2 py-0.5 bg-accent-purple/15 text-accent-purple rounded">
                            {cat.mentions}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trending Keywords */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-accent-blue" />
                      Keywords
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {trending.trendingKeywords?.slice(0, 15).map((kw, i) => (
                        <span
                          key={kw.keyword}
                          className="px-2 py-1 bg-bg-tertiary rounded text-sm"
                          style={{ opacity: 1 - (i * 0.05) }}
                        >
                          {kw.keyword} ({kw.count})
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="text-xs text-text-muted text-center">
                  Analyzed {trending.articlesAnalyzed} articles in the last {trending.timeWindow}
                </div>
              </div>
            )}

            {/* Loading State */}
            {loadingNews && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-accent-blue" />
                <span className="ml-2 text-text-muted">Loading news data...</span>
              </div>
            )}
          </div>
        )}

        {/* News Sources Info */}
        <div className="mt-4 pt-4 border-t border-border-color">
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>Sources: CoinDesk, The Block, Bitcoin Magazine, Cointelegraph, Decrypt, CryptoSlate, CryptoNews, U.Today, BeInCrypto</span>
            {marketSentiment?.lastUpdated && (
              <span>Updated: {new Date(marketSentiment.lastUpdated).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      </div>

      {/* Fee Tier Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <FeeTierDisplay feeTier={feeTier} />
        
        {/* Strategy Reference */}
        <div className="card p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 text-accent-blue" />
            Profile Strategy Guide
          </h4>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-accent-red/10 rounded-lg">
              <span className="font-semibold text-accent-red">HIGH:</span>
              <span className="text-text-secondary ml-2">Momentum breakouts on volatility expansion. 1.25% risk, 5x leverage max.</span>
            </div>
            <div className="p-3 bg-accent-yellow/10 rounded-lg">
              <span className="font-semibold text-accent-yellow">MEDIUM:</span>
              <span className="text-text-secondary ml-2">Trend pullbacks with regime filter. 0.5% risk, ADX &gt; 18 required.</span>
            </div>
            <div className="p-3 bg-accent-green/10 rounded-lg">
              <span className="font-semibold text-accent-green">LOW:</span>
              <span className="text-text-secondary ml-2">DCA + rebalancing. No leverage, volatility-adjusted sizing.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bot Configuration */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6 text-accent-yellow" />
            <div>
              <h3 className="font-display text-lg font-semibold">Automated Crypto Trading</h3>
              <p className="text-text-muted text-sm">Configure the bot to trade crypto automatically</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBotConfig(!showBotConfig)}
              className="btn-secondary flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              {showBotConfig ? 'Hide Settings' : 'Show Settings'}
            </button>
            <button
              onClick={() => setBotConfig(prev => ({ ...prev, cryptoEnabled: !prev.cryptoEnabled }))}
              className={`relative w-14 h-7 rounded-full transition-all ${
                botConfig.cryptoEnabled ? 'bg-accent-yellow' : 'bg-bg-tertiary'
              }`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                botConfig.cryptoEnabled ? 'left-8' : 'left-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Status indicator */}
        <div className={`p-3 rounded-lg mb-4 ${
          botConfig.cryptoEnabled 
            ? 'bg-accent-yellow/10 border border-accent-yellow/20' 
            : 'bg-bg-tertiary'
        }`}>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${botConfig.cryptoEnabled ? 'bg-accent-yellow animate-pulse' : 'bg-text-muted'}`} />
            <span className={botConfig.cryptoEnabled ? 'text-accent-yellow' : 'text-text-muted'}>
              {botConfig.cryptoEnabled ? 'Crypto bot is ENABLED' : 'Crypto bot is disabled'}
            </span>
          </div>
        </div>

        {showBotConfig && (
          <div className="space-y-6">
            {/* Config Tabs */}
            <div className="flex gap-2 border-b border-border-color pb-2">
              {[
                { key: 'general', label: 'General' },
                { key: 'risk', label: 'Risk Limits' },
                { key: 'profiles', label: 'Profile Settings' },
                { key: 'safety', label: 'Safety & Kill Switch' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setConfigTab(tab.key)}
                  className={`px-4 py-2 text-sm rounded-t-lg transition-all ${
                    configTab === tab.key
                      ? 'bg-accent-yellow/15 text-accent-yellow border-b-2 border-accent-yellow'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* General Tab */}
            {configTab === 'general' && (
              <div className="space-y-6">
                {/* Risk Profile Selection */}
                <div>
                  <h4 className="font-semibold mb-3">Active Risk Profile</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {['high', 'medium', 'low'].map(profile => (
                      <button
                        key={profile}
                        onClick={() => updateCryptoConfig('riskProfile', profile)}
                        className={`p-3 rounded-lg border transition-all ${
                          botConfig.cryptoConfig.riskProfile === profile
                            ? profile === 'high' ? 'bg-accent-red/15 border-accent-red text-accent-red' :
                              profile === 'medium' ? 'bg-accent-yellow/15 border-accent-yellow text-accent-yellow' :
                              'bg-accent-green/15 border-accent-green text-accent-green'
                            : 'border-border-color text-text-secondary hover:border-text-muted'
                        }`}
                      >
                        <div className="font-bold uppercase">{profile}</div>
                        <div className="text-xs mt-1 opacity-75">
                          {profile === 'high' ? 'Momentum Breakout' : 
                           profile === 'medium' ? 'Trend Pullback' : 'DCA + Rebalance'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Universe Selection */}
                <div>
                  <h4 className="font-semibold mb-3">Trading Universe</h4>
                  <select
                    value={botConfig.cryptoConfig.universe}
                    onChange={(e) => updateCryptoConfig('universe', e.target.value)}
                    className="input w-full"
                  >
                    <option value="core">Core (BTC + ETH only) - Safest</option>
                    <option value="majors">Majors (High liquidity alts)</option>
                    <option value="curated">Full Curated List (Max 15 symbols)</option>
                  </select>
                  <p className="text-xs text-text-muted mt-1">Max universe size: 15 symbols. Excludes coins &lt;90 days old.</p>
                </div>

                {/* DCA Settings */}
                <div>
                  <h4 className="font-semibold mb-3">DCA Settings (Low Risk)</h4>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                      <div>
                        <span className="font-medium">Enable DCA</span>
                        <p className="text-xs text-text-muted">Dollar-cost average into positions</p>
                      </div>
                      <button
                        onClick={() => updateCryptoConfig('enableDCA', !botConfig.cryptoConfig.enableDCA)}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          botConfig.cryptoConfig.enableDCA ? 'bg-accent-green' : 'bg-bg-card'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          botConfig.cryptoConfig.enableDCA ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-text-muted mb-1">DCA Frequency</label>
                        <select
                          value={botConfig.cryptoConfig.dcaFrequency}
                          onChange={(e) => updateCryptoConfig('dcaFrequency', e.target.value)}
                          className="input text-sm py-1.5 w-full"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Rebalance Frequency</label>
                        <select
                          value={botConfig.cryptoConfig.rebalanceFrequency}
                          onChange={(e) => updateCryptoConfig('rebalanceFrequency', e.target.value)}
                          className="input text-sm py-1.5 w-full"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Risk Limits Tab */}
            {configTab === 'risk' && (
              <div className="space-y-6">
                {/* Critical Warning */}
                <div className="p-4 bg-accent-red/10 border border-accent-red/30 rounded-lg">
                  <h4 className="font-semibold text-accent-red mb-2">Position Limits (CRITICAL)</h4>
                  <p className="text-xs text-text-secondary">Over-diversification kills accounts faster than under-diversification in crypto. These limits are enforced.</p>
                </div>

                {/* Max Positions Per Profile */}
                <div>
                  <h4 className="font-semibold mb-3">Max Open Positions</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {['high', 'medium', 'low'].map(profile => (
                      <div key={profile} className={`p-3 rounded-lg ${
                        profile === 'high' ? 'bg-accent-red/10' :
                        profile === 'medium' ? 'bg-accent-yellow/10' : 'bg-accent-green/10'
                      }`}>
                        <label className="block text-xs text-text-muted mb-1 capitalize">{profile} Risk</label>
                        <input
                          type="number"
                          value={botConfig.cryptoConfig.maxPositions?.[profile] || (profile === 'high' ? 5 : profile === 'medium' ? 4 : 3)}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: {
                              ...prev.cryptoConfig,
                              maxPositions: {
                                ...prev.cryptoConfig.maxPositions,
                                [profile]: parseInt(e.target.value)
                              }
                            }
                          }))}
                          min={1} max={profile === 'high' ? 5 : profile === 'medium' ? 4 : 3}
                          className="input text-sm py-1 w-full"
                        />
                        <p className="text-xs text-text-muted mt-1">Max: {profile === 'high' ? '3-5' : profile === 'medium' ? '2-4' : '1-3'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Max Daily Loss Per Profile */}
                <div>
                  <h4 className="font-semibold mb-3">Max Daily Loss %</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {['high', 'medium', 'low'].map(profile => (
                      <div key={profile}>
                        <label className="block text-xs text-text-muted mb-1 capitalize">{profile} Risk</label>
                        <input
                          type="number"
                          value={(botConfig.cryptoConfig.maxDailyLoss?.[profile] || 0.03) * 100}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: {
                              ...prev.cryptoConfig,
                              maxDailyLoss: {
                                ...prev.cryptoConfig.maxDailyLoss,
                                [profile]: parseFloat(e.target.value) / 100
                              }
                            }
                          }))}
                          min={1} max={10} step={0.5}
                          className="input text-sm py-1.5"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Max Account Drawdown */}
                <div>
                  <h4 className="font-semibold mb-3">Max Account Drawdown %</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {['high', 'medium', 'low'].map(profile => (
                      <div key={profile}>
                        <label className="block text-xs text-text-muted mb-1 capitalize">{profile} Risk</label>
                        <input
                          type="number"
                          value={(botConfig.cryptoConfig.maxAccountDrawdown?.[profile] || 0.15) * 100}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: {
                              ...prev.cryptoConfig,
                              maxAccountDrawdown: {
                                ...prev.cryptoConfig.maxAccountDrawdown,
                                [profile]: parseFloat(e.target.value) / 100
                              }
                            }
                          }))}
                          min={5} max={30} step={1}
                          className="input text-sm py-1.5"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Risk Per Trade */}
                <div>
                  <h4 className="font-semibold mb-3">Risk Per Trade %</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {['high', 'medium', 'low'].map(profile => (
                      <div key={profile}>
                        <label className="block text-xs text-text-muted mb-1 capitalize">{profile} Risk</label>
                        <input
                          type="number"
                          value={(botConfig.cryptoConfig.riskPerTrade?.[profile] || 0.005) * 100}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: {
                              ...prev.cryptoConfig,
                              riskPerTrade: {
                                ...prev.cryptoConfig.riskPerTrade,
                                [profile]: parseFloat(e.target.value) / 100
                              }
                            }
                          }))}
                          min={0.1} max={2} step={0.1}
                          className="input text-sm py-1.5"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Leverage Caps */}
                <div>
                  <h4 className="font-semibold mb-3">Leverage Caps by Coin</h4>
                  {['high', 'medium', 'low'].map(profile => (
                    <div key={profile} className="mb-4">
                      <h5 className={`text-sm font-medium mb-2 ${
                        profile === 'high' ? 'text-accent-red' :
                        profile === 'medium' ? 'text-accent-yellow' : 'text-accent-green'
                      }`}>{profile.charAt(0).toUpperCase() + profile.slice(1)} Risk Profile</h5>
                      <div className="grid grid-cols-3 gap-3">
                        {['BTC', 'ETH', 'alts'].map(coin => (
                          <div key={coin}>
                            <label className="block text-xs text-text-muted mb-1">{coin}</label>
                            <input
                              type="number"
                              value={botConfig.cryptoConfig.leverageCaps?.[profile]?.[coin] || 1}
                              onChange={(e) => setBotConfig(prev => ({
                                ...prev,
                                cryptoConfig: {
                                  ...prev.cryptoConfig,
                                  leverageCaps: {
                                    ...prev.cryptoConfig.leverageCaps,
                                    [profile]: {
                                      ...prev.cryptoConfig.leverageCaps?.[profile],
                                      [coin]: parseInt(e.target.value)
                                    }
                                  }
                                }
                              }))}
                              min={1} max={profile === 'high' ? 10 : profile === 'medium' ? 5 : 1}
                              className="input text-sm py-1 w-full"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Profile Settings Tab */}
            {configTab === 'profiles' && (
              <div className="space-y-6">
                {/* High Risk Settings */}
                <div className="p-4 bg-accent-red/10 border border-accent-red/30 rounded-lg">
                  <h4 className="font-semibold text-accent-red mb-4">High Risk - Momentum Breakout</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">BBW Percentile</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.highRisk?.bbwPercentile || 20}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, highRisk: { ...prev.cryptoConfig.highRisk, bbwPercentile: parseInt(e.target.value) }}
                        }))}
                        min={10} max={30}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Volume Z-Score</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.highRisk?.volumeZScore || 1.5}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, highRisk: { ...prev.cryptoConfig.highRisk, volumeZScore: parseFloat(e.target.value) }}
                        }))}
                        min={1} max={3} step={0.1}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Stop Loss ATR</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.highRisk?.stopLossATR || 1.5}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, highRisk: { ...prev.cryptoConfig.highRisk, stopLossATR: parseFloat(e.target.value) }}
                        }))}
                        min={1} max={3} step={0.1}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Trailing ATR</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.highRisk?.trailingATR || 3.0}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, highRisk: { ...prev.cryptoConfig.highRisk, trailingATR: parseFloat(e.target.value) }}
                        }))}
                        min={2} max={5} step={0.5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Max Re-entries/Day</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.highRisk?.maxReentries || 2}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, highRisk: { ...prev.cryptoConfig.highRisk, maxReentries: parseInt(e.target.value) }}
                        }))}
                        min={0} max={3}
                        className="input text-sm py-1.5"
                      />
                    </div>
                  </div>
                </div>

                {/* Medium Risk Settings */}
                <div className="p-4 bg-accent-yellow/10 border border-accent-yellow/30 rounded-lg">
                  <h4 className="font-semibold text-accent-yellow mb-4">Medium Risk - Trend Pullback</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">ADX Threshold</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.mediumRisk?.adxThreshold || 20}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, mediumRisk: { ...prev.cryptoConfig.mediumRisk, adxThreshold: parseInt(e.target.value) }}
                        }))}
                        min={15} max={30}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Stop Loss ATR</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.mediumRisk?.stopLossATR || 1.2}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, mediumRisk: { ...prev.cryptoConfig.mediumRisk, stopLossATR: parseFloat(e.target.value) }}
                        }))}
                        min={1} max={2} step={0.1}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Trailing ATR</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.mediumRisk?.trailingATR || 2.5}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, mediumRisk: { ...prev.cryptoConfig.mediumRisk, trailingATR: parseFloat(e.target.value) }}
                        }))}
                        min={2} max={4} step={0.5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                  </div>
                </div>

                {/* Low Risk Settings */}
                <div className="p-4 bg-accent-green/10 border border-accent-green/30 rounded-lg">
                  <h4 className="font-semibold text-accent-green mb-4">Low Risk - Capital Preservation</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Core Allocation %</label>
                      <input
                        type="number"
                        value={(botConfig.cryptoConfig.lowRisk?.coreAllocation || 0.80) * 100}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, lowRisk: { ...prev.cryptoConfig.lowRisk, coreAllocation: parseFloat(e.target.value) / 100 }}
                        }))}
                        min={50} max={100} step={5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Stable Buffer %</label>
                      <input
                        type="number"
                        value={(botConfig.cryptoConfig.lowRisk?.stableBuffer || 0.10) * 100}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, lowRisk: { ...prev.cryptoConfig.lowRisk, stableBuffer: parseFloat(e.target.value) / 100 }}
                        }))}
                        min={0} max={50} step={5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Risk-On ATR%</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.lowRisk?.riskOnATRPercent || 4.0}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, lowRisk: { ...prev.cryptoConfig.lowRisk, riskOnATRPercent: parseFloat(e.target.value) }}
                        }))}
                        min={2} max={6} step={0.5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Risk-Off ATR%</label>
                      <input
                        type="number"
                        value={botConfig.cryptoConfig.lowRisk?.riskOffATRPercent || 5.5}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, lowRisk: { ...prev.cryptoConfig.lowRisk, riskOffATRPercent: parseFloat(e.target.value) }}
                        }))}
                        min={4} max={8} step={0.5}
                        className="input text-sm py-1.5"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={botConfig.cryptoConfig.lowRisk?.allowShorts === true}
                        onChange={(e) => setBotConfig(prev => ({
                          ...prev,
                          cryptoConfig: { ...prev.cryptoConfig, lowRisk: { ...prev.cryptoConfig.lowRisk, allowShorts: e.target.checked }}
                        }))}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">Allow Shorts (Not Recommended)</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Safety Tab */}
            {configTab === 'safety' && (
              <div className="space-y-6">
                {/* Correlation Gating */}
                <div>
                  <label className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                    <div>
                      <span className="font-semibold">Correlation Gating</span>
                      <p className="text-xs text-text-muted mt-1">Only ONE position per correlated cluster (BTC cluster, ETH cluster, etc.)</p>
                    </div>
                    <button
                      onClick={() => updateCryptoConfig('correlationGatingEnabled', !botConfig.cryptoConfig.correlationGatingEnabled)}
                      className={`relative w-14 h-7 rounded-full transition-all ${
                        botConfig.cryptoConfig.correlationGatingEnabled ? 'bg-accent-green' : 'bg-bg-card'
                      }`}
                    >
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        botConfig.cryptoConfig.correlationGatingEnabled ? 'left-8' : 'left-1'
                      }`} />
                    </button>
                  </label>
                </div>

                {/* Correlation Clusters Info */}
                <div className="p-4 bg-bg-tertiary rounded-lg">
                  <h4 className="font-semibold mb-3">Correlation Clusters</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-2 bg-bg-card rounded">
                      <span className="text-accent-yellow font-semibold">BTC Cluster:</span>
                      <span className="text-text-muted ml-2">BTC, BCH, LTC</span>
                    </div>
                    <div className="p-2 bg-bg-card rounded">
                      <span className="text-accent-blue font-semibold">ETH Cluster:</span>
                      <span className="text-text-muted ml-2">ETH, OP, ARB, MATIC</span>
                    </div>
                    <div className="p-2 bg-bg-card rounded">
                      <span className="text-accent-purple font-semibold">Alt L1:</span>
                      <span className="text-text-muted ml-2">SOL, AVAX, ADA, DOT</span>
                    </div>
                    <div className="p-2 bg-bg-card rounded">
                      <span className="text-accent-red font-semibold">Meme:</span>
                      <span className="text-text-muted ml-2">DOGE, SHIB</span>
                    </div>
                  </div>
                </div>

                {/* Kill Switch */}
                <div>
                  <label className="flex items-center justify-between p-4 bg-accent-red/10 border border-accent-red/30 rounded-lg">
                    <div>
                      <span className="font-semibold text-accent-red">Kill Switch</span>
                      <p className="text-xs text-text-muted mt-1">Auto-halt trading on extreme conditions</p>
                    </div>
                    <button
                      onClick={() => updateCryptoConfig('killSwitchEnabled', !botConfig.cryptoConfig.killSwitchEnabled)}
                      className={`relative w-14 h-7 rounded-full transition-all ${
                        botConfig.cryptoConfig.killSwitchEnabled ? 'bg-accent-red' : 'bg-bg-card'
                      }`}
                    >
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        botConfig.cryptoConfig.killSwitchEnabled ? 'left-8' : 'left-1'
                      }`} />
                    </button>
                  </label>
                </div>

                {/* Kill Switch Thresholds */}
                {botConfig.cryptoConfig.killSwitchEnabled && (
                  <div className="p-4 bg-bg-tertiary rounded-lg">
                    <h4 className="font-semibold mb-3">Kill Switch Triggers</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Max Slippage Multiplier</label>
                        <input
                          type="number"
                          value={botConfig.cryptoConfig.killSwitch?.maxSlippageMultiplier || 3}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: { ...prev.cryptoConfig, killSwitch: { ...prev.cryptoConfig.killSwitch, maxSlippageMultiplier: parseFloat(e.target.value) }}
                          }))}
                          min={2} max={5} step={0.5}
                          className="input text-sm py-1.5"
                        />
                        <p className="text-xs text-text-muted mt-1">Halt if slippage &gt; Nx expected</p>
                      </div>
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Max Funding Rate %</label>
                        <input
                          type="number"
                          value={(botConfig.cryptoConfig.killSwitch?.maxFundingRate || 0.0015) * 100}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: { ...prev.cryptoConfig, killSwitch: { ...prev.cryptoConfig.killSwitch, maxFundingRate: parseFloat(e.target.value) / 100 }}
                          }))}
                          min={0.05} max={0.5} step={0.05}
                          className="input text-sm py-1.5"
                        />
                        <p className="text-xs text-text-muted mt-1">Per 8h funding limit</p>
                      </div>
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Max Latency (ms)</label>
                        <input
                          type="number"
                          value={botConfig.cryptoConfig.killSwitch?.maxLatencyMs || 5000}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: { ...prev.cryptoConfig, killSwitch: { ...prev.cryptoConfig.killSwitch, maxLatencyMs: parseInt(e.target.value) }}
                          }))}
                          min={1000} max={10000} step={500}
                          className="input text-sm py-1.5"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-text-muted mb-1">Min Liq Distance (x Stop)</label>
                        <input
                          type="number"
                          value={botConfig.cryptoConfig.killSwitch?.minLiquidationDistance || 1.5}
                          onChange={(e) => setBotConfig(prev => ({
                            ...prev,
                            cryptoConfig: { ...prev.cryptoConfig, killSwitch: { ...prev.cryptoConfig.killSwitch, minLiquidationDistance: parseFloat(e.target.value) }}
                          }))}
                          min={1} max={3} step={0.1}
                          className="input text-sm py-1.5"
                        />
                        <p className="text-xs text-text-muted mt-1">Liq must be Nx stop distance</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* EMA200 Filter */}
                <div>
                  <label className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                    <div>
                      <span className="font-semibold">EMA 200 Filter (Low Risk)</span>
                      <p className="text-xs text-text-muted mt-1">Don't buy when price is below EMA200</p>
                    </div>
                    <button
                      onClick={() => updateCryptoConfig('ema200Filter', !botConfig.cryptoConfig.ema200Filter)}
                      className={`relative w-14 h-7 rounded-full transition-all ${
                        botConfig.cryptoConfig.ema200Filter ? 'bg-accent-blue' : 'bg-bg-card'
                      }`}
                    >
                      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                        botConfig.cryptoConfig.ema200Filter ? 'left-8' : 'left-1'
                      }`} />
                    </button>
                  </label>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="p-4 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
              <h4 className="font-semibold text-accent-yellow mb-2">Current Profile: {botConfig.cryptoConfig.riskProfile?.toUpperCase()}</h4>
              <ul className="text-xs text-text-secondary space-y-1">
                <li>Max Positions: {botConfig.cryptoConfig.maxPositions?.[botConfig.cryptoConfig.riskProfile] || 4}</li>
                <li>Max Daily Loss: {((botConfig.cryptoConfig.maxDailyLoss?.[botConfig.cryptoConfig.riskProfile] || 0.03) * 100).toFixed(1)}%</li>
                <li>Risk Per Trade: {((botConfig.cryptoConfig.riskPerTrade?.[botConfig.cryptoConfig.riskProfile] || 0.005) * 100).toFixed(2)}%</li>
                <li>Correlation Gating: {botConfig.cryptoConfig.correlationGatingEnabled ? 'ON' : 'OFF'}</li>
                <li>Kill Switch: {botConfig.cryptoConfig.killSwitchEnabled ? 'ARMED' : 'OFF'}</li>
              </ul>
            </div>

            {/* Save Button */}
            <button
              onClick={saveBotConfig}
              disabled={savingBotConfig}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {savingBotConfig ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {savingBotConfig ? 'Saving...' : 'Save Crypto Bot Settings'}
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Crypto;
